// The client's jobs, quotes and invoices (finance-real, the user 2026-10-22), read and written as useClientPage's are: the
// reads sit under financeKeys.all, so useFinanceMutation refetches them after every write. A drawer re-reads its job's
// figures from that refetch, never from the form (TD-102). Each read and write calls lineJobService.
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import type { InvoiceJobsInput, JobInvoiceNumberInput, JobInvoicePaymentInput, JobInvoiceStatusInput, LineJobInput, LineJobPatch, LineJobPickedStatus, LineJobStatus, LineJobStatusInput, PriceReviewInput, QuoteBundleStatus, QuoteInput, QuoteStatusInput } from "../types";
import { financeKeys, useFinanceMutation } from "./financeQuery";
import { lineJobService } from "../services/lineJobService";

// `contract` names the user's job now (ENTITY_CONFIG).
const jobsWord = ENTITY_CONFIG.contract.plural.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();

export const lineJobKeys = {
  jobs: (clientId: string) => [...financeKeys.all, "line-jobs", clientId] as const,
  cost: (clientId: string, priceMinor: number, marginPct: number, jobId: string | null, paysAllToTeam = false) =>
    [...financeKeys.all, "line-job-cost", clientId, priceMinor, marginPct, jobId, paysAllToTeam] as const,
  invoices: (clientId: string) => [...financeKeys.all, "job-invoices", clientId] as const,
  agencyJobs: () => [...financeKeys.all, "agency-line-jobs"] as const,
  agencyInvoices: () => [...financeKeys.all, "agency-job-invoices"] as const,
};

/** /finance's Jobs: every client's jobs, under financeKeys.all, so a write on a client's page reads here too. */
export const useAgencyJobs = (enabled = true) => useQuery({ queryKey: lineJobKeys.agencyJobs(), queryFn: lineJobService.agencyJobs, enabled });

/** /finance's Invoices: every client's invoices. */
export const useAgencyInvoices = (enabled = true) => useQuery({ queryKey: lineJobKeys.agencyInvoices(), queryFn: lineJobService.agencyInvoices, enabled });

export const useClientJobs = (clientId: string | undefined) =>
  useQuery({ queryKey: lineJobKeys.jobs(clientId ?? "none"), queryFn: () => lineJobService.clientJobs(clientId ?? ""), enabled: !!clientId });

/** The form's cap and margin as the server works them out, once the price and margin % are valid: null leaves it unread.
 * Under financeKeys.all, so a claim added in the drawer reads here too. The last answer stays on screen while the next
 * keystroke's loads, so the figures never blank as the user types. */
export const useJobCost = (clientId: string, priceMinor: number | null, marginPct: number | null, jobId: string | null, paysAllToTeam = false) =>
  useQuery({
    queryKey: lineJobKeys.cost(clientId, priceMinor ?? 0, marginPct ?? 0, jobId, paysAllToTeam),
    queryFn: () => lineJobService.cost({ clientId, priceMinor: priceMinor ?? 0, marginPct: marginPct ?? 0, jobId, paysAllToTeam }),
    enabled: priceMinor !== null && marginPct !== null,
    // Only while it is read: a cleared price, or a margin % below the floor, shows no cost rather than the last one.
    placeholderData: priceMinor !== null && marginPct !== null ? keepPreviousData : undefined,
  });

export const useCreateJob = (clientId: string) =>
  useFinanceMutation((input: LineJobInput) => lineJobService.createJob(clientId, input), (job) =>
    job.status === "active" ? `${job.name} is active.` : job.status === "agreed" ? `${job.name} is agreed: its status starts it.` : `${job.name} is a draft.`,
  );

/** The job drawer's autosave: no toast either way, as the drawer shows Saving and Saved, and a refusal under its field. */
export const useUpdateJob = () =>
  useFinanceMutation((v: { jobId: string; patch: LineJobPatch }) => lineJobService.patchJob(v.jobId, v.patch), undefined, { refusedInline: true });

const STATUS_SET: Record<LineJobPickedStatus, string> = {
  draft: "is a draft again",
  agreed: "is agreed: its status starts it",
  active: "is active",
  paused: "is paused: its series makes no next one until it is active again",
  done: `is done: ready to put on an ${invoiceWord}`,
  dropped: "is dropped: it will not be done or billed",
};

/** The statuses a job can come back with from a pick: Quoting and Invoiced are set elsewhere. */
const isSet = (status: LineJobStatus): status is LineJobPickedStatus => status !== "quoting" && status !== "invoiced";

export const useSetJobStatus = () =>
  useFinanceMutation(
    (v: { jobId: string; input: LineJobStatusInput }) => lineJobService.setJobStatus(v.jobId, v.input),
    (job) => `${job.name} · ${job.periodLabel} ${isSet(job.status) ? STATUS_SET[job.status] : "is saved"}.`,
  );

export const useSetJobReview = () =>
  useFinanceMutation((v: { jobId: string; input: PriceReviewInput }) => lineJobService.setJobReview(v.jobId, v.input), (job) => (job.review ? "Flagged to review." : "Review cleared."));

export const useCreateQuote = (clientId: string) => useFinanceMutation((input: QuoteInput) => lineJobService.createQuote(clientId, input), (quote) => `${quote.title} is a draft.`);

export const useUpdateQuote = () =>
  useFinanceMutation((v: { quoteId: string; input: Partial<QuoteInput> }) => lineJobService.patchQuote(v.quoteId, v.input), (quote) => `${quote.title} is saved.`);

const STATUS_DONE: Record<QuoteBundleStatus, string> = {
  draft: `is a draft again: its ${jobsWord} are drafts`,
  proposed: `is proposed: its ${jobsWord} are quoting`,
  accepted: `is accepted: its ${jobsWord} are agreed, each to start from its own status`,
  declined: `is declined: its ${jobsWord} are dropped`,
};

export const useSetQuoteStatus = () =>
  useFinanceMutation((v: { quoteId: string; input: QuoteStatusInput }) => lineJobService.setQuoteStatus(v.quoteId, v.input), (quote) => `${quote.title} ${STATUS_DONE[quote.status]}.`);

export const useJobInvoices = (clientId: string | undefined) =>
  useQuery({ queryKey: lineJobKeys.invoices(clientId ?? "none"), queryFn: () => lineJobService.clientInvoices(clientId ?? ""), enabled: !!clientId });

export const useCreateInvoice = (clientId: string) =>
  useFinanceMutation((input: InvoiceJobsInput) => lineJobService.createInvoice(clientId, input), (invoice) => `The draft ${invoiceWord} holds ${invoice.jobIds.length} ${invoice.jobIds.length === 1 ? ENTITY_CONFIG.contract.label.toLowerCase() : jobsWord}.`);

export const useAddInvoiceJobs = () =>
  useFinanceMutation((v: { invoiceId: string; input: InvoiceJobsInput }) => lineJobService.addInvoiceJobs(v.invoiceId, v.input), () => `On the ${invoiceWord}.`);

/** The invoice drawer's New job, stacked over it: active, and on the draft, both or neither. A refusal shows in the form. */
export const useCreateJobOnInvoice = () =>
  useFinanceMutation(
    (v: { invoiceId: string; input: LineJobInput }) => lineJobService.createJobOnInvoice(v.invoiceId, v.input),
    () => `The new ${ENTITY_CONFIG.contract.label.toLowerCase()} is active, on the ${invoiceWord}.`,
    { refusedInline: true },
  );

export const useRemoveInvoiceJob = () =>
  useFinanceMutation((v: { invoiceId: string; jobId: string }) => lineJobService.removeInvoiceJob(v.invoiceId, v.jobId), (r) =>
    r.invoice ? `${r.job.name} is off the ${invoiceWord}: done, ready to invoice.` : `${r.job.name} is done, ready to invoice, and the empty draft is gone.`,
  );

/** An invoice's status as the user picks it (DD-062): Sent stamps the day and fixes the draft's prefilled number (answer
 * g), Draft takes it back. */
export const useSetInvoiceStatus = () =>
  useFinanceMutation(
    (v: { invoiceId: string; input: JobInvoiceStatusInput }) => lineJobService.setInvoiceStatus(v.invoiceId, v.input),
    (invoice) => (invoice.status === "sent" ? `${ENTITY_CONFIG.invoice.label} ${invoice.number ?? ""} is sent.` : `The ${invoiceWord} is a draft again.`),
  );

/** A draft's number, edited in its drawer: a refusal (a number taken) shows under the field. */
export const useSetInvoiceNumber = () =>
  useFinanceMutation((v: { invoiceId: string; input: JobInvoiceNumberInput }) => lineJobService.setInvoiceNumber(v.invoiceId, v.input), undefined, { refusedInline: true });

/** A payment on a sent invoice: its status follows, Partly paid or Paid, or Paid in full. */
export const useRecordInvoicePayment = () =>
  useFinanceMutation(
    (v: { invoiceId: string; input: JobInvoicePaymentInput }) => lineJobService.recordInvoicePayment(v.invoiceId, v.input),
    (invoice) => (invoice.status === "paid" ? `${ENTITY_CONFIG.invoice.label} ${invoice.number ?? ""} is paid.` : `Recorded: ${ENTITY_CONFIG.invoice.label.toLowerCase()} ${invoice.number ?? ""} is partly paid.`),
  );
